import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CreditCard, Megaphone, AlertTriangle, ArrowRight, IndianRupee } from 'lucide-react';

export default function ResidentDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/resident/dashboard').then(res => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="loading-spinner" /></div>;

  return (
    <div data-testid="resident-dashboard">
      {/* Welcome */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900 font-['Outfit']">
          Welcome, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">{data?.community_name || 'Your Community'}</p>
      </div>

      {/* Pending Dues Card */}
      <Card className="border-slate-100 mb-6 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white overflow-hidden" data-testid="pending-dues-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-xs font-medium">Pending Dues</p>
              <p className="text-3xl font-bold font-['Outfit'] mt-1 flex items-center gap-1">
                <IndianRupee size={24} />
                {(data?.pending_amount || 0).toLocaleString()}
              </p>
            </div>
            {data?.pending_amount > 0 && (
              <Link to="/app/pay-dues">
                <Button className="bg-white text-emerald-700 hover:bg-emerald-50 rounded-lg text-sm font-medium" data-testid="pay-now-btn">
                  Pay Now <ArrowRight size={14} className="ml-1" />
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Link to="/app/announcements">
          <Card className="border-slate-100 card-hover cursor-pointer" data-testid="quick-announcements">
            <CardContent className="p-4 flex flex-col items-center justify-center text-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Megaphone size={18} className="text-blue-600" />
              </div>
              <span className="text-xs font-medium text-slate-700">Announcements</span>
            </CardContent>
          </Card>
        </Link>
        <Link to="/app/pay-dues">
          <Card className="border-slate-100 card-hover cursor-pointer" data-testid="quick-pay-dues">
            <CardContent className="p-4 flex flex-col items-center justify-center text-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <CreditCard size={18} className="text-emerald-600" />
              </div>
              <span className="text-xs font-medium text-slate-700">Pay Dues</span>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Announcements */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900 font-['Outfit']">Latest Notices</h2>
          <Link to="/app/announcements" className="text-xs text-emerald-600 font-medium">View All</Link>
        </div>
        <div className="space-y-3">
          {data?.announcements?.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No announcements yet</p>}
          {data?.announcements?.slice(0, 3).map(a => (
            <Card key={a.announcement_id} className="border-slate-100" data-testid={`dashboard-ann-${a.announcement_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  {a.priority === 'urgent' ? (
                    <AlertTriangle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                  ) : (
                    <Megaphone size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-sm font-medium text-slate-900 truncate">{a.title}</h3>
                      {a.priority === 'urgent' && <Badge className="bg-red-50 text-red-700 text-[10px] border-red-100">Urgent</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2">{a.content}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Recent Payments */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-slate-900 font-['Outfit']">Payment History</h2>
          <Link to="/app/pay-dues" className="text-xs text-emerald-600 font-medium">View All</Link>
        </div>
        <div className="space-y-2">
          {data?.payments?.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No payments yet</p>}
          {data?.payments?.slice(0, 3).map(p => (
            <Card key={p.payment_id} className="border-slate-100" data-testid={`dashboard-payment-${p.payment_id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">{p.description}</p>
                  <p className="text-xs text-slate-500">Due: {p.due_date}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-900">₹{p.amount?.toLocaleString()}</p>
                  <Badge className={`text-[10px] ${p.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                    {p.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
